/**
 * Settings tab for the Admin Dashboard.
 * Extracted from AdminDashboard.jsx (was ~635 inline lines).
 *
 * One outer SettingsPanel orchestrates load/save against /api/admin/settings,
 * a stack of sub-panels each owns a slice of the settings doc. The flat
 * `form` + `upd` API is preserved exactly from the original so the back-end
 * contract is unchanged.
 */
import React, { useEffect, useState } from "react";
import {
    Phone,
    CreditCard,
    Sparkles,
    Globe,
    Star,
    ChefHat,
    MapPin,
    Megaphone,
    Trash2,
} from "lucide-react";
import api from "../../api";
import { useApp } from "../../AppContext";
import { toast } from "sonner";
import {
    Field,
    TextArea,
    Toggle,
    SectionCard,
    ColorField,
    PaymentBlock,
} from "./AdminFormPrimitives";

const COUNTRY_OPTIONS = [
    { code: "DR", name_es: "Rep. Dominicana", name_en: "Dominican Republic", flag: ["#002D62", "#FFFFFF", "#CE1126"] },
    { code: "JM", name_es: "Jamaica", name_en: "Jamaica", flag: ["#009A49", "#FED100", "#000000"] },
    { code: "MX", name_es: "México", name_en: "Mexico", flag: ["#006847", "#FFFFFF", "#CE1126"] },
    { code: "IT", name_es: "Italia", name_en: "Italy", flag: ["#009246", "#FFFFFF", "#CE2B37"] },
    { code: "CO", name_es: "Colombia", name_en: "Colombia", flag: ["#FCD116", "#003893", "#CE1126"] },
    { code: "PR", name_es: "Puerto Rico", name_en: "Puerto Rico", flag: ["#0050F0", "#FFFFFF", "#FF0000"] },
    { code: "NONE", name_es: "Sin bandera (genérico)", name_en: "No flag (generic)", flag: ["#3A2C1F", "#FAF7F2", "#C85A17"] },
];

const CURRENCY_PRESETS = [
    { code: "USD", symbol: "$", label: "US Dollar ($)" },
    { code: "JMD", symbol: "J$", label: "Jamaican Dollar (J$)" },
    { code: "DOP", symbol: "RD$", label: "Dominican Peso (RD$)" },
    { code: "MXN", symbol: "$", label: "Mexican Peso ($)" },
    { code: "EUR", symbol: "€", label: "Euro (€)" },
    { code: "GBP", symbol: "£", label: "Pound (£)" },
    { code: "COP", symbol: "$", label: "Colombian Peso ($)" },
    { code: "ARS", symbol: "$", label: "Argentine Peso ($)" },
    { code: "BRL", symbol: "R$", label: "Brazilian Real (R$)" },
    { code: "INR", symbol: "₹", label: "Indian Rupee (₹)" },
    { code: "CAD", symbol: "C$", label: "Canadian Dollar (C$)" },
];

const BrandPreview = ({ form }) => {
    const { lang } = useApp();
    return (
        <div className="rounded-2xl border border-earth-200 bg-earth-50/40 p-4 flex items-center gap-3">
            <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-serif font-bold shadow-md"
                style={{ background: form.brand_color_primary }}
            >
                {form.brand_initials || "X"}
            </div>
            <div>
                <div className="font-serif font-bold text-earth-900">
                    {form.brand_name || (lang === "es" ? "Tu Restaurante" : "Your Restaurant")}
                </div>
                <div className="text-xs" style={{ color: form.brand_color_primary }}>
                    {form.brand_tagline || (lang === "es" ? "Tu Ciudad" : "Your City")}
                </div>
            </div>
            <button
                type="button"
                disabled
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs font-bold uppercase tracking-wider"
                style={{ background: form.brand_color_primary }}
            >
                {lang === "es" ? "Vista previa CTA" : "CTA preview"}
            </button>
        </div>
    );
};

const BrandingPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard
            icon={Sparkles}
            title={t.admin.sectionBrand}
            subtitle={t.admin.sectionBrandSub}
            accent={form.brand_color_primary || "#C85A17"}
        >
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Nombre del restaurante" : "Restaurant name"} value={form.brand_name} onChange={upd("brand_name")} testId="brand-name-input" />
                <Field label={lang === "es" ? "Tagline / Subtítulo" : "Tagline / Subtitle"} value={form.brand_tagline} onChange={upd("brand_tagline")} testId="brand-tagline-input" />
                <Field label={lang === "es" ? "Iniciales del logo (2–3 caracteres)" : "Logo initials (2–3 chars)"} value={form.brand_initials} onChange={(v) => upd("brand_initials")(String(v).slice(0, 3))} testId="brand-initials-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <ColorField label={lang === "es" ? "Color primario" : "Primary color"} value={form.brand_color_primary} onChange={upd("brand_color_primary")} testId="brand-color-primary" />
                <ColorField label={lang === "es" ? "Color acento" : "Accent color"} value={form.brand_color_accent} onChange={upd("brand_color_accent")} testId="brand-color-accent" />
            </div>
            <BrandPreview form={form} />
        </SectionCard>
    );
};

const CountryDecorationsPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={Globe} title={t.admin.sectionCountry} subtitle={t.admin.sectionCountrySub}>
            <div className="flex items-center justify-between gap-4">
                <div className="font-serif font-bold text-earth-900">
                    {lang === "es" ? "Mostrar decoraciones culturales" : "Show cultural decorations"}
                </div>
                <Toggle
                    checked={form.decorations_enabled}
                    onChange={upd("decorations_enabled")}
                    testId="decorations-enabled"
                    labelOn={lang === "es" ? "Visibles" : "Visible"}
                    labelOff={lang === "es" ? "Ocultas" : "Hidden"}
                />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {COUNTRY_OPTIONS.map((c) => {
                    const active = form.country_code === c.code;
                    return (
                        <button
                            type="button"
                            key={c.code}
                            onClick={() => upd("country_code")(c.code)}
                            data-testid={`country-${c.code}-btn`}
                            className={`flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${
                                active ? "border-terracotta-500 bg-terracotta-50" : "border-earth-200 hover:border-earth-300 bg-white"
                            }`}
                        >
                            <div className="flex flex-col h-6 w-8 overflow-hidden rounded-sm shadow ring-1 ring-earth-200">
                                <div className="flex-1" style={{ background: c.flag[0] }} />
                                <div className="flex-1" style={{ background: c.flag[1] }} />
                                <div className="flex-1" style={{ background: c.flag[2] }} />
                            </div>
                            <div>
                                <div className="font-bold text-earth-900 text-sm">{lang === "es" ? c.name_es : c.name_en}</div>
                                <div className="text-[10px] uppercase tracking-wider text-earth-500">{c.code}</div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </SectionCard>
    );
};

const HeroPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={Star} title={t.admin.sectionHero} subtitle={t.admin.sectionHeroSub}>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Título principal (ES)" : "Main headline (ES)"} value={form.hero_headline_es} onChange={upd("hero_headline_es")} testId="hero-headline-es" />
                <Field label={lang === "es" ? "Título principal (EN)" : "Main headline (EN)"} value={form.hero_headline_en} onChange={upd("hero_headline_en")} testId="hero-headline-en" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TextArea label={lang === "es" ? "Subtítulo (ES)" : "Subtitle (ES)"} value={form.hero_subtitle_es} onChange={upd("hero_subtitle_es")} testId="hero-subtitle-es" rows={4} />
                <TextArea label={lang === "es" ? "Subtítulo (EN)" : "Subtitle (EN)"} value={form.hero_subtitle_en} onChange={upd("hero_subtitle_en")} testId="hero-subtitle-en" rows={4} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Botón principal (ES)" : "Primary CTA (ES)"} value={form.hero_cta_primary_es} onChange={upd("hero_cta_primary_es")} testId="hero-cta-primary-es" />
                <Field label={lang === "es" ? "Botón principal (EN)" : "Primary CTA (EN)"} value={form.hero_cta_primary_en} onChange={upd("hero_cta_primary_en")} testId="hero-cta-primary-en" />
                <Field label={lang === "es" ? "Botón secundario (ES)" : "Secondary CTA (ES)"} value={form.hero_cta_secondary_es} onChange={upd("hero_cta_secondary_es")} testId="hero-cta-secondary-es" />
                <Field label={lang === "es" ? "Botón secundario (EN)" : "Secondary CTA (EN)"} value={form.hero_cta_secondary_en} onChange={upd("hero_cta_secondary_en")} testId="hero-cta-secondary-en" />
            </div>
            <Field
                label={lang === "es" ? "URL de imagen del hero (opcional — vacío usa la artwork del chef por defecto)" : "Hero image URL (optional — blank uses default chef artwork)"}
                value={form.hero_image_url}
                onChange={upd("hero_image_url")}
                testId="hero-image-url"
            />
        </SectionCard>
    );
};

const HeroBadgesPanel = ({ form, upd }) => {
    const { t } = useApp();
    return (
        <SectionCard
            icon={Sparkles}
            title={t.admin.sectionHeroBadges}
            subtitle={t.admin.sectionHeroBadgesSub}
            accent={form.brand_color_primary || "#C85A17"}
        >
            {[1, 2, 3].map((i) => (
                <div key={i} className="grid sm:grid-cols-2 gap-4">
                    <Field label={`Badge ${i} (ES)`} value={form[`hero_badge${i}_es`]} onChange={upd(`hero_badge${i}_es`)} testId={`hero-badge${i}-es`} />
                    <Field label={`Badge ${i} (EN)`} value={form[`hero_badge${i}_en`]} onChange={upd(`hero_badge${i}_en`)} testId={`hero-badge${i}-en`} />
                </div>
            ))}
        </SectionCard>
    );
};

const AboutPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={Star} title={t.admin.sectionAbout} subtitle={t.admin.sectionAboutSub}>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Título (ES)" : "Title (ES)"} value={form.about_title_es} onChange={upd("about_title_es")} testId="about-title-es" />
                <Field label={lang === "es" ? "Título (EN)" : "Title (EN)"} value={form.about_title_en} onChange={upd("about_title_en")} testId="about-title-en" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TextArea label={lang === "es" ? "Párrafo 1 (ES)" : "Paragraph 1 (ES)"} value={form.about_body1_es} onChange={upd("about_body1_es")} testId="about-body1-es" rows={4} placeholder={lang === "es" ? "Vacío = copy por defecto" : "Empty = default copy"} />
                <TextArea label={lang === "es" ? "Párrafo 1 (EN)" : "Paragraph 1 (EN)"} value={form.about_body1_en} onChange={upd("about_body1_en")} testId="about-body1-en" rows={4} placeholder={lang === "es" ? "Vacío = copy por defecto" : "Empty = default copy"} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TextArea label={lang === "es" ? "Párrafo 2 (ES)" : "Paragraph 2 (ES)"} value={form.about_body2_es} onChange={upd("about_body2_es")} testId="about-body2-es" rows={4} placeholder={lang === "es" ? "Vacío = copy por defecto" : "Empty = default copy"} />
                <TextArea label={lang === "es" ? "Párrafo 2 (EN)" : "Paragraph 2 (EN)"} value={form.about_body2_en} onChange={upd("about_body2_en")} testId="about-body2-en" rows={4} placeholder={lang === "es" ? "Vacío = copy por defecto" : "Empty = default copy"} />
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-earth-500">
                {lang === "es" ? "3 estadísticas destacadas" : "3 featured stats"}
            </div>
            {[1, 2, 3].map((i) => (
                <div key={i} className="grid sm:grid-cols-3 gap-4 p-3 rounded-2xl border border-earth-200 bg-earth-50/40">
                    <Field label={lang === "es" ? `Stat ${i} valor (ej. 20+)` : `Stat ${i} value (e.g. 20+)`} value={form[`about_stat${i}_value`]} onChange={upd(`about_stat${i}_value`)} testId={`about-stat${i}-value`} />
                    <Field label={lang === "es" ? "Etiqueta (ES)" : "Label (ES)"} value={form[`about_stat${i}_label_es`]} onChange={upd(`about_stat${i}_label_es`)} testId={`about-stat${i}-label-es`} />
                    <Field label={lang === "es" ? "Etiqueta (EN)" : "Label (EN)"} value={form[`about_stat${i}_label_en`]} onChange={upd(`about_stat${i}_label_en`)} testId={`about-stat${i}-label-en`} />
                </div>
            ))}
        </SectionCard>
    );
};

const ChefPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={ChefHat} title={t.admin.sectionChef} subtitle={t.admin.sectionChefSub}>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Nombre del chef" : "Chef name"} value={form.chef_name} onChange={upd("chef_name")} testId="chef-name" />
                <Field label={lang === "es" ? "URL de foto del chef (opcional)" : "Chef photo URL (optional)"} value={form.chef_image_url} onChange={upd("chef_image_url")} testId="chef-image-url" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Subtítulo del chef (ES)" : "Chef subtitle (ES)"} value={form.chef_subtitle_es} onChange={upd("chef_subtitle_es")} testId="chef-subtitle-es" />
                <Field label={lang === "es" ? "Subtítulo del chef (EN)" : "Chef subtitle (EN)"} value={form.chef_subtitle_en} onChange={upd("chef_subtitle_en")} testId="chef-subtitle-en" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TextArea label={lang === "es" ? "Bio del chef (ES)" : "Chef bio (ES)"} value={form.chef_bio_es} onChange={upd("chef_bio_es")} testId="chef-bio-es" rows={6} />
                <TextArea label={lang === "es" ? "Bio del chef (EN)" : "Chef bio (EN)"} value={form.chef_bio_en} onChange={upd("chef_bio_en")} testId="chef-bio-en" rows={6} />
            </div>
        </SectionCard>
    );
};

const ContactPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={MapPin} title={t.admin.sectionContact} subtitle={t.admin.sectionContactSub}>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Ciudad / país" : "City / country"} value={form.contact_city} onChange={upd("contact_city")} testId="contact-city" />
                <Field label={lang === "es" ? "Dirección completa (opcional)" : "Full address (optional)"} value={form.contact_address} onChange={upd("contact_address")} testId="contact-address" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label={lang === "es" ? "Horario (ES)" : "Hours (ES)"} value={form.contact_hours_es} onChange={upd("contact_hours_es")} testId="contact-hours-es" />
                <Field label={lang === "es" ? "Horario (EN)" : "Hours (EN)"} value={form.contact_hours_en} onChange={upd("contact_hours_en")} testId="contact-hours-en" />
            </div>
        </SectionCard>
    );
};

const AnnouncementPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    return (
        <SectionCard icon={Megaphone} title={t.admin.sectionAnnouncement} subtitle={t.admin.sectionAnnouncementSub}>
            <div className="flex items-center justify-between gap-4">
                <div className="font-serif font-bold text-earth-900">{lang === "es" ? "Mostrar banner" : "Show banner"}</div>
                <Toggle
                    checked={form.announcement_enabled}
                    onChange={upd("announcement_enabled")}
                    testId="announcement-enabled"
                    labelOn={t.admin.visible}
                    labelOff={t.admin.hidden}
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <TextArea label={lang === "es" ? "Texto banner (ES)" : "Banner text (ES)"} value={form.announcement_text_es} onChange={upd("announcement_text_es")} testId="announcement-text-es" rows={3} placeholder={lang === "es" ? "Vacío = mensaje por defecto" : "Empty = default message"} />
                <TextArea label={lang === "es" ? "Texto banner (EN)" : "Banner text (EN)"} value={form.announcement_text_en} onChange={upd("announcement_text_en")} testId="announcement-text-en" rows={3} placeholder={lang === "es" ? "Vacío = mensaje por defecto" : "Empty = default message"} />
            </div>
        </SectionCard>
    );
};

const CurrencyPanel = ({ form, upd }) => {
    const { t, lang } = useApp();
    const sample = form.currency_position === "after"
        ? `12.50 ${form.currency_symbol || "$"}`
        : `${form.currency_symbol || "$"}12.50`;
    return (
        <SectionCard icon={CreditCard} title={t.admin.sectionCurrency} subtitle={t.admin.sectionCurrencySub}>
            <div className="grid sm:grid-cols-3 gap-4 items-end">
                <Field label={lang === "es" ? "Símbolo (p. ej. $ / € / ₹)" : "Symbol (e.g. $ / € / ₹)"} value={form.currency_symbol} onChange={upd("currency_symbol")} testId="currency-symbol" />
                <Field label={lang === "es" ? "Código ISO (USD, JMD, DOP, EUR…)" : "ISO code (USD, JMD, DOP, EUR…)"} value={form.currency_code} onChange={(v) => upd("currency_code")(String(v).toUpperCase().slice(0, 5))} testId="currency-code" />
                <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                        {lang === "es" ? "Posición del símbolo" : "Symbol position"}
                    </label>
                    <div className="inline-flex w-full rounded-xl border border-earth-200 overflow-hidden">
                        {[
                            { val: "before", label: lang === "es" ? "Antes ($12)" : "Before ($12)" },
                            { val: "after", label: lang === "es" ? "Después (12 $)" : "After (12 $)" },
                        ].map((o) => (
                            <button
                                key={o.val}
                                type="button"
                                onClick={() => upd("currency_position")(o.val)}
                                data-testid={`currency-pos-${o.val}`}
                                className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                    form.currency_position === o.val
                                        ? "bg-terracotta-500 text-white"
                                        : "bg-earth-50 hover:bg-earth-100 text-earth-700"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {CURRENCY_PRESETS.map((c) => (
                    <button
                        type="button"
                        key={c.code}
                        onClick={() => {
                            upd("currency_symbol")(c.symbol);
                            upd("currency_code")(c.code);
                        }}
                        data-testid={`currency-preset-${c.code}`}
                        className={`p-2.5 rounded-xl border-2 text-left text-xs transition-all ${
                            form.currency_code === c.code
                                ? "border-terracotta-500 bg-terracotta-50"
                                : "border-earth-200 hover:border-earth-300 bg-white"
                        }`}
                    >
                        <div className="font-bold text-earth-900">{c.label}</div>
                        <div className="text-[10px] uppercase tracking-wider text-earth-500">{c.code}</div>
                    </button>
                ))}
            </div>
            <div className="rounded-2xl border border-earth-200 bg-earth-50/40 p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-earth-500 mb-1">
                    {lang === "es" ? "Vista previa" : "Preview"}
                </div>
                <div className="font-serif font-black text-2xl text-terracotta-600">{sample}</div>
            </div>
        </SectionCard>
    );
};

const WipeOrdersButton = () => {
    const { t } = useApp();
    const [wiping, setWiping] = useState(false);
    const wipe = async () => {
        const first = window.prompt(t.admin.wipeOrdersConfirm);
        if (!first || !["ELIMINAR", "DELETE"].includes(first.trim().toUpperCase())) {
            if (first !== null) toast.error(t.admin.wipeOrdersTypeWrong);
            return;
        }
        if (!window.confirm(t.admin.wipeOrdersFinal)) return;
        setWiping(true);
        try {
            const r = await api.post("/admin/orders/bulk-delete", {
                older_than_days: 0,
                include_catering: true,
            });
            const orders = r.data?.deleted ?? 0;
            const cat = r.data?.deleted_catering ?? 0;
            toast.success(t.admin.wipeOrdersDone.replace("{orders}", orders).replace("{catering}", cat));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setWiping(false);
        }
    };
    return (
        <button
            onClick={wipe}
            disabled={wiping}
            data-testid="settings-wipe-orders-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-bold transition-colors"
        >
            <Trash2 className="w-4 h-4" />
            {wiping ? t.admin.saving : t.admin.wipeOrders}
        </button>
    );
};

const SettingsPanel = () => {
    const { t, lang } = useApp();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/admin/settings");
                if (!cancelled) setForm(r.data || {});
            } catch (err) {
                if (!cancelled) toast.error(err?.response?.data?.detail || "Error");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/admin/settings", form);
            setForm(r.data);
            toast.success(t.common.success);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        } finally {
            setSaving(false);
        }
    };

    if (loading || !form) return <div className="text-earth-600">{t.common.loading}</div>;

    const formattedWA = (() => {
        const d = String(form.whatsapp_number || "").replace(/\D/g, "");
        if (d.length === 11 && d.startsWith("1")) return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
        if (d.length === 10) return `+1 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
        return d ? `+${d}` : "";
    })();

    return (
        <section data-testid="admin-settings-panel" className="space-y-8">
            <div>
                <h2 className="font-serif text-3xl font-bold text-earth-900 mb-2">{t.admin.settingsTitle}</h2>
                <p className="text-sm text-earth-600">{t.admin.settingsSubtitle}</p>
            </div>

            <BrandingPanel form={form} upd={upd} />
            <CurrencyPanel form={form} upd={upd} />
            <CountryDecorationsPanel form={form} upd={upd} />
            <HeroPanel form={form} upd={upd} />
            <HeroBadgesPanel form={form} upd={upd} />
            <AboutPanel form={form} upd={upd} />
            <ChefPanel form={form} upd={upd} />
            <ContactPanel form={form} upd={upd} />
            <AnnouncementPanel form={form} upd={upd} />

            {/* WhatsApp business number */}
            <div className="bg-white rounded-3xl border border-earth-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-leaf-500/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-leaf-600" />
                    </div>
                    <div>
                        <h3 className="font-serif text-xl font-bold text-earth-900">{t.admin.sectionWhatsApp}</h3>
                        <p className="text-xs text-earth-600">{t.admin.sectionWhatsAppSub}</p>
                    </div>
                </div>
                <Field
                    label={lang === "es" ? "WhatsApp (+código país y número)" : "WhatsApp (+country code and number)"}
                    value={form.whatsapp_number}
                    onChange={upd("whatsapp_number")}
                    testId="settings-whatsapp-input"
                />
                {formattedWA && (
                    <div className="mt-2 text-xs text-earth-500">
                        <span className="font-mono text-earth-800">{formattedWA}</span>
                    </div>
                )}
            </div>

            {/* Payment methods */}
            <div className="bg-white rounded-3xl border border-earth-200 p-6 shadow-sm space-y-5">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-full bg-terracotta-500/10 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-terracotta-600" />
                    </div>
                    <div>
                        <h3 className="font-serif text-xl font-bold text-earth-900">{t.admin.sectionPayments}</h3>
                        <p className="text-xs text-earth-600">{t.admin.sectionPaymentsSub}</p>
                    </div>
                </div>

                <PaymentBlock title="Lynk (Jamaica)" enabled={form.lynk_enabled} onToggle={upd("lynk_enabled")} testIdPrefix="lynk">
                    <Field label={lang === "es" ? "Teléfono Lynk" : "Lynk phone"} value={form.lynk_phone} onChange={upd("lynk_phone")} testId="settings-lynk-phone" />
                    <Field label={lang === "es" ? "Nombre del titular" : "Holder name"} value={form.lynk_name} onChange={upd("lynk_name")} testId="settings-lynk-name" />
                </PaymentBlock>

                <PaymentBlock title="Tpago (Rep. Dominicana)" enabled={form.tpago_enabled} onToggle={upd("tpago_enabled")} testIdPrefix="tpago">
                    <Field label={lang === "es" ? "Teléfono Tpago" : "Tpago phone"} value={form.tpago_phone} onChange={upd("tpago_phone")} testId="settings-tpago-phone" />
                    <Field label={lang === "es" ? "Email Tpago" : "Tpago email"} value={form.tpago_email} onChange={upd("tpago_email")} testId="settings-tpago-email" />
                </PaymentBlock>

                <PaymentBlock title="PayPal" enabled={form.paypal_enabled} onToggle={upd("paypal_enabled")} testIdPrefix="paypal">
                    <Field label={lang === "es" ? "Email PayPal" : "PayPal email"} value={form.paypal_email} onChange={upd("paypal_email")} testId="settings-paypal-email" />
                </PaymentBlock>

                <PaymentBlock
                    title={lang === "es" ? "Efectivo (al recoger / entregar)" : "Cash (pickup / delivery)"}
                    enabled={form.cash_enabled}
                    onToggle={upd("cash_enabled")}
                    testIdPrefix="cash"
                />
            </div>

            <div className="flex justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="settings-save-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-terracotta-500 hover:bg-terracotta-600 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                >
                    {saving ? t.admin.saving : t.admin.saveChanges}
                </button>
            </div>

            {/* Danger zone */}
            <div className="bg-white rounded-3xl border-2 border-red-300 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                        <h3 className="font-serif text-xl font-bold text-red-700">{t.admin.dangerZone}</h3>
                        <p className="text-xs text-earth-600">{t.admin.dangerZoneSub}</p>
                    </div>
                </div>
                <WipeOrdersButton />
            </div>
        </section>
    );
};

export default SettingsPanel;
